/**
 * intelligenceMethods.ts — the single source of truth for the Intelligence
 * Methods page (`/methodology`).
 *
 * GROUND RULE FOR THIS FILE: every entry must correspond to a technique that is
 * actually implemented in this repository, and `source` must be a real path.
 * This page is shown to auditors; one invented method discredits the rest. When
 * a technique is removed from the backend, remove its entry here in the same PR.
 *
 * Every entry below was verified against the code at the time of writing —
 * thresholds quoted are the constants in the cited module, not documentation.
 * Where a module's own docstring disagreed with its code, the code won.
 *
 * Paths are repository-root-relative, forward-slashed.
 */

export interface IntelligenceMethod {
  /** The technique, named the way a statistician would name it. */
  name: string;
  /** One sentence, plain English: what question does this answer? */
  purpose: string;
  /** Repo-relative path to the module that implements it. */
  source: string;
  /** Where the output of this method surfaces in the product. */
  surfaces: string[];
  /**
   * Present ONLY when the technique is implemented but not running by default.
   * Rendered as an amber caveat. Being straight about what is switched off is
   * the whole point of this page — never delete a caveat to make the list look
   * stronger.
   */
  caveat?: string;
}

export interface IntelligenceLayer {
  id: string;
  /** Display ordinal, e.g. "01". Dormant layers are not numbered. */
  number: string;
  title: string;
  /** What this layer is for, and how much evidential weight it carries. */
  tagline: string;
  /**
   * 'dormant' — implemented and running on every transaction, but starved of an
   * upstream data feed, so it returns nothing. Kept visible on purpose: hiding
   * a capability gap from an auditor is worse than admitting it.
   */
  status?: 'dormant';
  methods: IntelligenceMethod[];
}

export const INTELLIGENCE_LAYERS: IntelligenceLayer[] = [
  {
    id: 'deterministic',
    number: '01',
    title: 'Deterministic physics and law',
    tagline:
      '76 codified rules across 13 families, of which roughly 28 currently fire — the rest are listed at the foot of this page with the exact data feed they are waiting on. Each rule is a fixed, restatable test against physics, the vehicle spec sheet, or South African regulation: no model, no training data, no drift. If one fires, it can be re-derived by hand in front of a court.',
    methods: [
      {
        name: 'Tank-capacity overflow test',
        purpose:
          'Flags a fill larger than the vehicle can physically hold — litres dispensed above 1.2× the OEM tank capacity, so the surplus went into something other than the tank.',
        source: 'domain/fraud_rules/volume_rules.py',
        surfaces: ['Fraud Hub', 'Alert Detail', 'Rule Health'],
      },
      {
        name: 'Dual-tank capacity resolution',
        purpose:
          'Combines main and auxiliary tank capacity before any volume test, and refuses to run the test at all when neither capacity is known rather than assuming a fleet-average tank.',
        source: 'services/rule_engine/context.py',
        surfaces: ['Fraud Hub', 'Alert Detail'],
      },
      {
        name: 'Haversine impossible-travel test',
        purpose:
          'Great-circle distance between consecutive fills divided by elapsed time; an implied speed above 150 km/h means the same card was used in two places it could not have travelled between.',
        source: 'domain/fraud_rules/time_rules.py',
        surfaces: ['Fraud Hub', 'Fraud Map', 'Alert Detail'],
      },
      {
        name: 'Card-cloning simultaneity test',
        purpose:
          'Detects one card presented at two stations at least 50 km apart within 10 minutes — physically impossible for a single card, so a duplicate exists.',
        source: 'domain/fraud_rules/card_rules.py',
        surfaces: ['Fraud Hub', 'Offender Vehicles', 'Alert Detail'],
      },
      {
        name: 'Round-value modulo test',
        purpose:
          'Oil purchases that are exact multiples of R5 — and more suspiciously R25, the airtime denomination — indicating a keyed-in cash amount rather than a metered dispense.',
        source: 'domain/fraud_rules/oil_rules.py',
        surfaces: ['Fraud Hub', 'Merchant Ops', 'Alert Detail'],
      },
      {
        name: 'Odometer monotonicity suite',
        purpose:
          'Six independent tests on the odometer sequence — rollback, stagnant reading, implausible jump, sequence break, zero reading, and distance/fuel mismatch — because a tampered odometer is the precondition for most consumption fraud.',
        source: 'domain/fraud_rules/odometer_rules.py',
        surfaces: ['Fraud Hub', 'Vehicle Detail', 'Data Quality'],
      },
      {
        name: 'Damerau-Levenshtein odometer typo classifier',
        purpose:
          'Separates a genuine odometer anomaly from a keying error by testing whether the reading is a single edit — transposition, dropped digit, doubled digit — away from a plausible value, so honest mistakes do not become fraud alerts.',
        source: 'domain/data_quality/odometer_typo.py',
        surfaces: ['Data Quality', 'Alert Detail'],
      },
      {
        name: 'After-hours temporal window',
        purpose:
          'Fills between 22:00 and 05:00, when a fleet vehicle should be parked and no authorised driver is on shift.',
        source: 'domain/fraud_rules/time_rules.py',
        surfaces: ['Fraud Hub', 'Fraud Dashboard'],
      },
      {
        name: 'Rapid-succession and split-fill tests',
        purpose:
          'Two fills on one vehicle within 30 minutes, or a cluster inside a 4-hour window, which is the standard way of dodging a per-transaction limit.',
        source: 'domain/fraud_rules/time_rules.py',
        surfaces: ['Fraud Hub', 'Offender Vehicles'],
      },
      {
        name: 'Card-velocity threshold',
        purpose:
          'Counts transactions per vehicle in a rolling 24 hours and flags at four or more — a legitimate fleet vehicle does not refuel that often.',
        source: 'domain/fraud_rules/card_rules.py',
        surfaces: ['Fraud Hub', 'Offender Vehicles'],
      },
      {
        name: 'Regulated zone-price conformance (DMRE)',
        purpose:
          'Compares the unit price paid against the gazetted DMRE price for that fuel grade and price zone in that month — petrol is price-regulated in South Africa, so paying over it is an arithmetic fact, not an opinion.',
        source: 'domain/fraud_rules/price_rules.py',
        surfaces: ['Fuel Merchants', 'Merchant Ops', 'Fraud Hub'],
      },
      {
        name: 'Station price inference for unzoned sites',
        purpose:
          'Where a station has no regulated zone, infers its own pump price from the median of observed unit prices over 90 days and only tests against it when the sample is large and tight enough to trust.',
        source: 'ops/alembic/versions/20260711_0036_pr_rules_real_prices.py',
        surfaces: ['Fuel Merchants', 'Merchant Ops'],
      },
      {
        name: 'Excess-oil-versus-sump test',
        purpose:
          'Flags an oil purchase larger than 1.2× the engine sump can hold — you cannot put eight litres into a five-litre engine, so the surplus was something else.',
        source: 'domain/fraud_rules/oil_rules.py',
        surfaces: ['Fraud Hub', 'Merchant Ops'],
      },
      {
        name: 'Station-pattern tests',
        purpose:
          'Six tests on where a vehicle fuels relative to its own history — driving past closer stations, over-90% loyalty to one site, station hopping, and sites with their own alert history.',
        source: 'domain/fraud_rules/station_rules.py',
        surfaces: ['Fuel Merchants', 'Fraud Hub', 'Merchant Ops'],
      },
      {
        name: 'Graceful rule degradation with skip audit',
        purpose:
          'Every rule declares the data it needs; when telematics or odometer is missing the rule is skipped and the reason recorded, so a silent data gap never masquerades as a clean vehicle.',
        source: 'services/rule_engine/orchestrator.py',
        surfaces: ['Rule Health', 'Data Quality'],
      },
    ],
  },
  {
    id: 'baselines',
    number: '02',
    title: 'Learned baselines and behavioural profiling',
    tagline:
      'What is normal for this vehicle, this peer group, this driver? Rules need a reference point, and a fleet-wide average is a bad one. These build the reference from the fleet’s own history, and are explicit about how much evidence sits behind each number.',
    methods: [
      {
        name: 'Per-vehicle consumption baseline (mean ± σ)',
        purpose:
          'Nightly recompute of each vehicle’s own arithmetic mean and sample standard deviation of L/100km over a 90-day window, requiring at least five valid legs before the baseline is marked usable.',
        source: 'workers/tasks/baselines.py',
        surfaces: ['Consumption', 'Vehicle Detail', 'Fleet Health'],
      },
      {
        name: 'Five-tier hierarchical baseline resolver',
        purpose:
          'Falls back in strict order — the vehicle’s own history, then its peer cohort, then the OEM spec, then its segment, then a global default — and labels every answer with the tier it came from so a weak baseline is never mistaken for a strong one.',
        source: 'domain/baselines/resolver.py',
        surfaces: ['Consumption', 'Alert Detail', 'Fleet Health'],
      },
      {
        name: 'Orphan-baseline flagging',
        purpose:
          'Marks any baseline that fell all the way through to a segment-global or global default as an orphan path, so findings resting on a guess are visibly weaker than findings resting on the vehicle’s own history.',
        source: 'domain/baselines/resolver.py',
        surfaces: ['Fleet Health', 'Alert Detail'],
      },
      {
        name: 'Peer-cohort percentile profiles (p25 / p50 / p75)',
        purpose:
          'Builds median and quartile consumption for six nested groupings — from ADG variant and zone down to make and vehicle class — over 180 days, so a vehicle is judged against genuinely comparable vehicles.',
        source: 'workers/tasks/baselines.py',
        surfaces: ['Consumption', 'Departments', 'Alert Detail'],
      },
      {
        name: 'ADG variant matching with confidence floor',
        purpose:
          'Matches each fleet vehicle to its Auto Dealers Guide variant — same engine, gearbox and trim — and only trusts the OEM figure when the match confidence clears a stated floor.',
        source: 'workers/tasks/oem_match.py',
        surfaces: ['Consumption', 'Fleet Health'],
      },
      {
        name: 'Driver efficiency delta (median-based)',
        purpose:
          'Measures each driver’s consumption as a percentage offset from what their vehicles should use, so the effect follows the driver across vehicle changes rather than being blamed on the car.',
        source: 'workers/tasks/baselines.py',
        surfaces: ['Departments', 'Consumption'],
      },
      {
        name: 'Altitude, terrain and vehicle-age correction factors',
        purpose:
          'Adjusts the expected consumption for the physics of where the vehicle actually operates — thinner air at altitude, elevation gain on the route, and engine wear by year — before any deviation is called abnormal.',
        source: 'domain/baselines/resolver.py',
        surfaces: ['Consumption', 'Alert Detail'],
      },
      {
        name: 'Odometer-span consumption pairing',
        purpose:
          'Derives true consumption from consecutive fill pairs — litres over the odometer delta between them — instead of trusting any single transaction’s self-reported efficiency.',
        source: 'services/analytics/fuel_span.py',
        surfaces: ['Consumption', 'Vehicle Detail'],
      },
      {
        name: 'Eight-reason physical-plausibility quarantine',
        purpose:
          'Every span that is physically impossible — reversed odometer, sub-kilometre delta, over 2 000 km between fills, suspected rollover at the 999999 ceiling — is quarantined with its reason rather than silently dropped or silently averaged in.',
        source: 'db/migrations/sql/2026_06_28_compute_fuel_span_pairs.sql',
        surfaces: ['Data Quality', 'Consumption'],
      },
      {
        name: 'Nightly sum-of-spans canary audit',
        purpose:
          'Independently re-derives total distance from the span table each night and compares it to the raw odometer record, so a silent regression in the consumption pipeline is caught by the platform before it is caught by a court.',
        source: 'scripts/span_canary.py',
        surfaces: ['Data Quality', 'Rule Health'],
      },
      {
        name: 'Rolling five-fill efficiency trend',
        purpose:
          'Tracks the last five fills to catch a vehicle drifting away from its own recent behaviour, which surfaces a developing siphoning pattern earlier than a 90-day baseline can.',
        source: 'services/rule_engine/context.py',
        surfaces: ['Consumption', 'Vehicle Detail'],
      },
      {
        name: 'Percentage-deviation consumption tests',
        purpose:
          'The live consumption rules fire at stated percentage deviations from baseline — 30% above, 25% below, and 40% after adjusting for highway versus city driving — rather than at an unexplainable model score.',
        source: 'domain/fraud_rules/consumption_rules.py',
        surfaces: ['Consumption', 'Fraud Hub', 'Alert Detail'],
      },
      {
        name: 'Standard-deviation band deviation (z-score)',
        purpose:
          'Expresses a vehicle’s 90-day consumption as the number of standard deviations from its own baseline, escalating severity past three, four and six sigma.',
        source: 'services/rule_engine/rules/span_backed_cp_rules.py',
        surfaces: ['Consumption'],
        caveat:
          'Implemented but off by default — the span-backed rule family is behind a feature flag pending backfill.',
      },
      {
        name: 'Station and attendant activity profiles',
        purpose:
          'Rolling 30-day counters per station and per attendant — unique vehicles, unique drivers, alert history — that give the collusion rules a reference point for what a normal forecourt looks like.',
        source: 'workers/tasks/baselines.py',
        surfaces: ['Merchant Ops', 'Fuel Merchants'],
      },
    ],
  },
  {
    id: 'statistical',
    number: '03',
    title: 'Unsupervised statistical learning',
    tagline:
      'One model, named: scikit-learn Isolation Forest. It is unsupervised — it learns each vehicle’s own multivariate normal behaviour from unlabelled history, so it needs no adjudicated fraud cases to work. It ranks and prioritises; it never convicts on its own.',
    methods: [
      {
        name: 'Isolation Forest multivariate anomaly scoring',
        purpose:
          'An ensemble of 100 random isolation trees learns the joint shape of each vehicle’s normal fills, so a transaction that is unremarkable on every single field but odd in combination still gets isolated.',
        source: 'services/ml/anomaly_detector.py',
        surfaces: ['Fraud Hub', 'Alert Detail', 'Vehicle Detail'],
      },
      {
        name: 'Eight-feature transaction vector',
        purpose:
          'Each fill is described by litres, distance since last fill, hour, weekday, price per litre, great-circle distance from the previous station, VAT ratio and hours since the previous fill.',
        source: 'services/ml/anomaly_detector.py',
        surfaces: ['Alert Detail'],
      },
      {
        name: 'Per-vehicle model with peer-cohort fallback',
        purpose:
          'Trains a dedicated model for any vehicle with at least 20 fills in 90 days, and falls back to a model trained across its ADG peer cohort below that — so a new vehicle is judged against its peers, not against the whole fleet.',
        source: 'services/ml/anomaly_detector.py',
        surfaces: ['Fraud Hub', 'Vehicle Detail'],
      },
      {
        name: 'Degenerate-variance feature suppression',
        purpose:
          'Drops any feature whose variance collapses in the training window, which stops a known data artefact — historical timestamps pinned to midnight — from being learned as if it were real behaviour.',
        source: 'services/ml/anomaly_detector.py',
        surfaces: ['Data Quality'],
      },
      {
        name: 'Min-max score normalisation to [0, 1]',
        purpose:
          'Rescales the raw isolation score against the training set’s own observed range and inverts it, so every vehicle’s score means the same thing on one comparable 0-to-1 axis.',
        source: 'services/ml/anomaly_detector.py',
        surfaces: ['Fraud Hub', 'Offender Vehicles'],
      },
      {
        name: 'Model registry provenance',
        purpose:
          'Records the scikit-learn version, training sample count, contamination rate and model version behind every score, so any historical alert can be traced to the exact model that produced it.',
        source: 'ops/alembic/versions/20260506_0008_ml_anomaly_score.py',
        surfaces: ['Alert Detail', 'Rule Health'],
      },
      {
        name: 'Score-to-severity banding',
        purpose:
          'Converts the continuous anomaly score into the same Critical/High/Medium/Low ladder the deterministic rules use, at published cut-points, so an ML finding is never presented on a different scale to a physics finding.',
        source: 'services/rule_engine/context.py',
        surfaces: ['Fraud Hub', 'Fraud Dashboard'],
      },
    ],
  },
  {
    id: 'llm',
    number: '04',
    title: 'Language layer under guardrails',
    tagline:
      'The Detective explains findings in English and answers questions in natural language. It is the least trusted component in the platform and is engineered that way: it cannot reach data it is not entitled to, it cannot state a number that is not in the retrieved records, and refusal is a first-class answer rather than a failure.',
    methods: [
      {
        name: 'AST-validated natural-language SQL',
        purpose:
          'Every generated query is parsed into a syntax tree with sqlglot and accepted only if it is structurally a SELECT — so no rewording of an UPDATE or DELETE can slip through, because they parse to a different node type entirely.',
        source: 'services/llm/sql_llm.py',
        surfaces: ['Ask AI'],
      },
      {
        name: 'Schema allowlist with deny-by-default',
        purpose:
          'Walks every table reference in the tree and rejects anything outside the six approved analytics schemas, including any unqualified table name, so nothing resolves implicitly to somewhere unintended.',
        source: 'services/llm/sql_llm.py',
        surfaces: ['Ask AI'],
      },
      {
        name: 'Structurally enforced tenant scoping',
        purpose:
          'Each SELECT must carry a tenant predicate bound to a server-side parameter — a hardcoded tenant literal is rejected, and the language model never sees the tenant identifier at all, so it cannot address another organisation’s data even in principle.',
        source: 'services/llm/sql_llm.py',
        surfaces: ['Ask AI'],
      },
      {
        name: 'Read-only execution with statement timeout',
        purpose:
          'Validated SQL runs inside a genuinely read-only transaction with a hard 30-second cap and an injected row limit, so even a query that passed every check cannot write, lock or exhaust the warehouse.',
        source: 'services/llm/sql_llm.py',
        surfaces: ['Ask AI'],
      },
      {
        name: 'pgvector cosine retrieval with confidence bands',
        purpose:
          'Retrieves supporting documentation by cosine similarity over 768-dimension embeddings and grades the result high, medium or low against published similarity floors, so thin evidence is visible rather than hidden.',
        source: 'services/llm/rag/retriever.py',
        surfaces: ['Ask AI'],
      },
      {
        name: 'Numeric hallucination scrubber',
        purpose:
          'Extracts every number, rand amount, rule code and identifier from the Detective’s answer and requires exact membership in the set drawn from the actual retrieved records — a figure that is merely plausible is not allowed through.',
        source: 'services/llm/hallucination_guard.py',
        surfaces: ['Ask AI'],
      },
      {
        name: 'Server-owned aggregate computation',
        purpose:
          'All totals and averages quoted in an answer are computed server-side and overwrite whatever the model produced, so arithmetic is never delegated to a language model.',
        source: 'services/llm/computed_metrics.py',
        surfaces: ['Ask AI'],
      },
      {
        name: 'Four-rung verdict ladder',
        purpose:
          'Every answer lands on refuse, inconclusive, probable or confident — refusal is rung zero of the scale, not an error path, so declining to answer is a legitimate and recorded outcome.',
        source: 'api/v1/schemas/ask_ai.py',
        surfaces: ['Ask AI', 'Alert Detail'],
      },
      {
        name: 'Seven-path pre-flight refusal classifier',
        purpose:
          'Classifies questions the platform must decline — out of scope, too little evidence, known data-quality trip, disciplinary escalation, weak retrieval — before the model is called at all, so a question already declined is never even put to it.',
        source: 'services/llm/refusal.py',
        surfaces: ['Ask AI'],
      },
      {
        name: 'Deterministic confidence caps',
        purpose:
          'Applies server-side ceilings for thin evidence, single-category findings and flagged data quality, taking the lowest, and downgrades the verdict to match — the trusted server score always beats the model’s own claim about itself.',
        source: 'services/llm/confidence.py',
        surfaces: ['Ask AI'],
      },
      {
        name: 'Mandatory counter-hypothesis',
        purpose:
          'A probable or confident verdict is structurally invalid without at least one stated innocent explanation, so the Detective is required to argue against itself before it accuses.',
        source: 'api/v1/schemas/ask_ai.py',
        surfaces: ['Ask AI'],
      },
      {
        name: 'Calibration harness — precision and expected calibration error',
        purpose:
          'Replays adjudicated cases to measure whether stated confidence matches observed precision, requiring at least 95% precision and an expected calibration error under 0.10 before the top verdict band may ever open.',
        source: 'services/calibration/harness.py',
        surfaces: ['Ombudsman API'],
        caveat:
          'Run offline on demand rather than on a schedule — in practice the confident band stays closed until an operator runs it.',
      },
      {
        name: 'Tenant-scoped fail-closed verdict gate',
        purpose:
          'Reads the latest calibration result for that specific organisation at answer time and downgrades a confident verdict when no passing result exists — one tenant’s good calibration can never unlock the band for another.',
        source: 'services/calibration/gate.py',
        surfaces: ['Ask AI', 'Ombudsman API'],
      },
      {
        name: 'Append-only audit store with seven-year retention',
        purpose:
          'Every answer is written to an audit table where database triggers block edits to all but the feedback columns and block truncation outright, retained for seven years to match the transaction record.',
        source: 'ops/alembic/versions/20260628_0025_audit_append_only_triggers.py',
        surfaces: ['Ombudsman API'],
      },
      {
        name: 'PII column redaction by SHA-256',
        purpose:
          'Driver, card, passport, token, email, phone, IMEI, name and address columns are hashed before the model or the browser ever sees a row, and aliasing them to an innocuous name is blocked at the SQL layer.',
        source: 'services/llm/pii_redactor.py',
        surfaces: ['Ask AI'],
      },
      {
        name: 'Server-authored map coordinates',
        purpose:
          'Any location the Detective plots is authored server-side from the fraud geography table and coordinates written by the model are stripped, so a map pin can never point at an invented place.',
        source: 'api/v1/routers/ask_ai.py',
        surfaces: ['Ask AI', 'Fraud Map'],
      },
      {
        name: 'Chart template allowlist',
        purpose:
          'A suggested chart must match a registered template with known columns and no PII, and every accept or reject decision is logged with its reason.',
        source: 'services/llm/chart_templates.py',
        surfaces: ['Ask AI'],
      },
      {
        name: 'Retrieval audit trail',
        purpose:
          'Logs which document chunks were retrieved, at what similarity, and in which confidence band for every question — so the evidence behind an answer can be reconstructed later, not just the answer.',
        source: 'services/llm/rag/retriever.py',
        surfaces: ['Ombudsman API'],
      },
    ],
  },
  {
    id: 'forecasting',
    number: '05',
    title: 'Forecasting',
    tagline:
      'Budget and volume projection for the coming quarter, with the uncertainty stated rather than implied — and with a named, conventional model rather than an unexplainable one.',
    methods: [
      {
        name: 'SARIMA (0,1,1)(0,1,1)[12] airline model',
        purpose:
          'Projects fleet spend and litres three months ahead using seasonal differencing on twelve monthly periods, which handles both the underlying trend and the annual fuel-consumption cycle.',
        source: 'services/ml/forecaster.py',
        surfaces: ['Forecasting'],
      },
      {
        name: '95% prediction intervals',
        purpose:
          'Publishes an upper and lower bound with every projection from the model’s own state-space variance, so the forecast is read as a range rather than mistaken for a promise.',
        source: 'services/ml/forecaster.py',
        surfaces: ['Forecasting'],
      },
      {
        name: 'Seasonal-naive fallback with empirical bands',
        purpose:
          'When there is under two years of history or the seasonal fit fails, falls back to the same month last year scaled by the median year-on-year ratio, with bands derived from observed log-variation.',
        source: 'services/ml/forecaster.py',
        surfaces: ['Forecasting'],
      },
      {
        name: 'Plausibility rejection guard',
        purpose:
          'Discards any fit that projects wildly beyond historical range and falls back to the simpler method, so a numerically unstable model can never publish an absurd budget figure.',
        source: 'services/ml/forecaster.py',
        surfaces: ['Forecasting'],
      },
      {
        name: 'Model-label provenance',
        purpose:
          'Stores which of the two methods actually produced each number, along with the training window and month count, so the page can say how the figure was reached.',
        source: 'ops/alembic/versions/20260626_0018_ml_forecast_result.py',
        surfaces: ['Forecasting'],
      },
    ],
  },
  {
    id: 'data-trust',
    number: '06',
    title: 'Data trust and self-scrutiny',
    tagline:
      'A detection platform that does not measure its own input quality will confidently accuse people on the strength of bad data. These methods score the evidence base itself and publish the result, including when it is poor.',
    methods: [
      {
        name: 'Field completeness scoring',
        purpose:
          'Measures the fill rate of every critical column over twelve months, so a rule that looks quiet because the data it needs is missing is visibly distinguished from a rule that is quiet because nothing is wrong.',
        source: 'api/v1/routers/data_quality.py',
        surfaces: ['Data Quality'],
      },
      {
        name: 'Synthetic-timestamp detection',
        purpose:
          'Counts transactions landing at exactly midnight to the second — the signature of a date-only feed given a fabricated time — because every time-of-day rule is unreliable on those rows.',
        source: 'api/v1/routers/dashboards.py',
        surfaces: ['Fleet Health', 'Data Quality'],
      },
      {
        name: 'Composite weighted trust score',
        purpose:
          'Combines missing baselines, missing geography, synthetic timestamps, missing vehicle dimensions and baseline freshness into one published 0-100 score with fixed weights, and returns no score at all rather than a misleading perfect one when there is no data.',
        source: 'api/v1/routers/dashboards.py',
        surfaces: ['Fleet Health'],
      },
      {
        name: 'Rule-health and suppression audit',
        purpose:
          'Tracks which rules are firing, which are dormant for want of a data feed, and which alerts were suppressed and why — so a silent detector is surfaced rather than mistaken for a clean fleet.',
        source: 'services/rule_engine/suppression_middleware.py',
        surfaces: ['Rule Health'],
      },
    ],
  },
  {
    id: 'fact-layer',
    number: '07',
    title: 'Evidence and aggregation layer',
    tagline:
      'Every figure on every dashboard is aggregated server-side from the transaction record. Nothing is estimated in the browser, and the arithmetic is chosen so that totals cannot quietly mislead.',
    methods: [
      {
        name: 'Litres-weighted averages',
        purpose:
          'Every average rand-per-litre is a sum-over-sum, never an average of averages, and ships with the sample size behind it so a figure resting on three fills is not read like one resting on thirty thousand.',
        source: 'api/v1/_helpers/weighted.py',
        surfaces: ['Overview', 'Consumption', 'Departments'],
      },
      {
        name: 'Pre-aggregated fact views',
        purpose:
          'Fraud counts, consumption and spend are materialised at fixed grains ahead of time, so a dashboard reads small exact rollups rather than re-scanning a 25-million-row transaction table on every page load.',
        source: 'ops/alembic/versions/20260714_0044_fact_layer_fraud_alert_daily_by_dim.py',
        surfaces: ['Fraud Hub', 'Fraud Dashboard', 'Consumption'],
      },
      {
        name: 'South African time-zone day bucketing',
        purpose:
          'Every daily bucket is cut on the SAST calendar day rather than UTC, so a fill at 01:00 in Johannesburg is counted on the day it actually happened.',
        source: 'ops/alembic/versions/20260714_0044_fact_layer_fraud_alert_daily_by_dim.py',
        surfaces: ['Fraud Hub', 'Fraud Dashboard'],
      },
      {
        name: 'Unknown-preserving dimension joins',
        purpose:
          'Alerts whose vehicle has no recorded make or model are counted into an explicit Unknown bucket instead of being dropped, so the totals on a breakdown always reconcile to the headline number.',
        source: 'ops/alembic/versions/20260714_0044_fact_layer_fraud_alert_daily_by_dim.py',
        surfaces: ['Fraud Hub', 'Consumption'],
      },
      {
        name: 'Data-anchored reporting windows',
        purpose:
          'Reporting periods are anchored to the newest transaction in the warehouse rather than to the wall clock, so a stalled feed shows as stale data instead of as a sudden drop in fraud.',
        source: 'api/v1/routers/data_quality.py',
        surfaces: ['Overview', 'Fraud Hub', 'Data Quality'],
      },
    ],
  },
  {
    id: 'dormant',
    number: '—',
    status: 'dormant',
    title: 'Built, and waiting on a data feed',
    tagline:
      'These are coded, imported, and evaluated against every transaction — and they return nothing, every time, because a field they depend on is absent or masked upstream. They are listed here rather than quietly omitted: an auditor is entitled to know what this platform cannot currently see. Each one names the exact feed that would switch it on.',
    methods: [
      {
        name: 'Pump-attendant collusion family',
        purpose:
          'Five tests linking a driver to one specific pump attendant — repeat pairings, station lock, driver affinity and off-route detours to reach one person — which is the classic signature of an inside job.',
        source: 'domain/fraud_rules/attendant_rules.py',
        surfaces: ['Merchant Ops'],
        caveat:
          'Cannot fire: the source feed masks attendant and driver ID to a fixed sentinel before it reaches this platform. There is no collusion detection today, and this needs a data-sharing change upstream rather than an engineering fix here.',
      },
      {
        name: 'GPS presence verification',
        purpose:
          'Compares vehicle position against the station forecourt at transaction time — beyond 500 m the vehicle was not at its own refuel, and beyond 2 km the fill is treated as a ghost.',
        source: 'domain/fraud_rules/station_rules.py',
        surfaces: ['Fraud Map', 'Telematics'],
        caveat:
          'Cannot fire: no live GPS/telematics feed is connected to this deployment. Ghost Fill and GPS Mismatch both check for a GPS context key and return nothing when it is absent.',
      },
      {
        name: 'Toll GPS and image corroboration',
        purpose:
          'Corroborates gantry crossings against vehicle position and number-plate images — phantom tolls with no vehicle nearby, impossible travel between gantries, tag-versus-plate mismatch, and concurrent distant crossings that indicate a cloned transponder.',
        source: 'domain/fraud_rules/toll_rules.py',
        surfaces: ['Fraud Map', 'Fraud Hub'],
        caveat:
          'Cannot fire: needs the same missing GPS/telematics feed, plus enforcement camera images for the tag-versus-plate test.',
      },
      {
        name: 'Route-based movement family',
        purpose:
          'Six tests over the route between consecutive fills using a Valhalla-computed expected drive time — excessive dwell, backward movement, region drift, fills over 200 km from home base, cross-province patterns and weekend border runs.',
        source: 'domain/fraud_rules/movement_rules.py',
        surfaces: ['Maps'],
        caveat:
          'Cannot fire: the vehicle-movement table these read is populated by route computation that depends on the same absent GPS feed.',
      },
      {
        name: 'Oil-grade conformance tests',
        purpose:
          'Checks the oil grade charged against the grade the engine actually takes, and against the gazetted statutory markup ceiling for that grade.',
        source: 'domain/fraud_rules/oil_rules.py',
        surfaces: ['Merchant Ops'],
        caveat:
          'Cannot fire: oil grade is null on every row in the current feed. The dependency is declared in the schema, so the gap shows on the data-quality dashboard rather than passing silently.',
      },
      {
        name: 'Relationship co-occurrence features',
        purpose:
          'A nightly edge table over card-to-attendant, card-to-station, attendant-to-station and vehicle-to-card pairings, with exclusivity, round-amount and off-hours ratios per relationship.',
        source: 'workers/tasks/network.py',
        surfaces: ['Merchant Ops'],
        caveat:
          'Computed nightly but read by nothing in the alert pipeline, and its attendant-keyed edges depend on the same masked identity field. These features influence no alert today.',
      },
    ],
  },
];

/** Layers that are live. Excludes anything starved of a data feed. */
export const LIVE_LAYERS = INTELLIGENCE_LAYERS.filter(
  (l) => l.status !== 'dormant',
);

/** Total number of methods across every layer. */
export function countMethods(layers: IntelligenceLayer[]): number {
  return layers.reduce((sum, layer) => sum + layer.methods.length, 0);
}
